import { CLIENT_MESSAGE_TYPE } from '@orn/global/assistant/wireProtocol.js';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import {
  appendStreamingDelta,
  confirmPendingMessages,
  dropStreamingBubble,
  finalizeStreamingBubble,
  insertAssistantReply,
  reconcileSnapshotMessages,
  snapshotToConversation,
} from '../lib/assistantSnapshot';
import { getApiBaseUrl } from '../lib/getEnv';

import type { IObservationDocument } from '@orn/global/assistant/snapshotMessage.js';
import type { ServerMessage } from '@orn/global/assistant/wireProtocol.js';

const generateId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/** Initial backoff before the first auto-reconnect attempt after an unexpected close. */
const RECONNECT_INITIAL_DELAY_MS = 1000;

/**
 * Cap on the exponential backoff between auto-reconnect attempts. The schedule
 * is 1s → 2s → 4s → 8s → 16s → 30s → 30s … so the cap kicks in on attempt 6.
 */
const RECONNECT_MAX_DELAY_MS = 30_000;

/**
 * Debug log for the WS connection lifecycle. Routes through `logger.debug`
 * (which uses `console.debug` and skips Sentry) so the trail sits under
 * DevTools' "Verbose" filter — visible when investigating reconnect
 * behavior, quiet by default.
 */
const debugLog = (message: string, context?: Record<string, unknown>): void => {
  const suffix = context ? ` ${JSON.stringify(context)}` : '';
  logger.debug(`[assistant-ws] ${message}${suffix}`);
};

/**
 * Structured fields for the close diagnostic log line. Surfaces the real close
 * `code`/`reason`/`wasClean` so an idle-timeout drop (expected `1006`) is
 * distinguishable from a server-initiated or clean close. Empty `reason`
 * collapses to `undefined` so it doesn't clutter the log.
 */
export function describeCloseEvent(event: Pick<CloseEvent, 'code' | 'reason' | 'wasClean'>): {
  code: number;
  reason: string | undefined;
  wasClean: boolean;
} {
  return { code: event.code, reason: event.reason || undefined, wasClean: event.wasClean };
}

// --- UI types ---

/**
 * A single turn in the rendered conversation transcript (user OR assistant).
 * Distinct from backend's `IAssistantMessage` wire envelope, which only carries
 * the assistant's content.
 *
 * Three independent fields describe a user bubble's state machine:
 *  - `clientMessageId` — durable identity. Set on every user message the
 *    client originates (UUID at send time) and echoed back on
 *    `transcript_snapshot.user.clientMessageId` for snapshot-derived rows.
 *    Used by the snapshot reconciler to dedupe by id.
 *  - `isPending` — true from send until the matching `assistant_message`
 *    arrives. Cleared on confirmation. The snapshot reconciler preserves
 *    only pending bubbles across replays.
 *  - `isInFlight` — true when the server is actively processing this
 *    bubble's turn; false when the bubble is queued locally waiting for the
 *    previous turn to complete (mid-turn typing). The view renders
 *    in-flight bubbles ABOVE the thinking box and queued bubbles BELOW it,
 *    so a user typing follow-ups while the assistant is replying sees the
 *    layout reflect "this is what I'm waiting on" vs "this hasn't started
 *    yet". Only meaningful while `isPending` is true.
 *
 * Splitting these avoids overloading one field with multiple meanings —
 * snapshot-derived messages carry a `clientMessageId` (durable) but are
 * never pending or in-flight.
 */
export interface IConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  clientMessageId?: string;
  isPending?: boolean;
  isInFlight?: boolean;
  /**
   * True while this assistant bubble is being grown token-by-token from
   * `assistant_message_delta` frames, before its round's terminal
   * `assistant_message` snaps it to the authoritative text. The only home for
   * "a bubble is mid-stream": the view derives the inline-vs-standalone spinner
   * from it, and `endTurn` drops any bubble still carrying it. Assistant-only;
   * never persisted, never on the wire.
   */
  isStreaming?: boolean;
  /**
   * Documents the user attached on this turn. Populated locally on `sendMessage`
   * from the composer's resolved `documentId` chips, and from
   * `transcript_snapshot.user.attachments` on rehydration. Renders as a chip
   * row above the user bubble in `AssistantMessage`.
   */
  attachments?: IObservationDocument[];
}

export interface IToolExecution {
  id: string;
  status: 'running' | 'complete' | 'error';
  /**
   * Tool name, args, and full output. Populated only for viewers who pass the
   * `debug-assistant` gate (the server omits them otherwise), since tool detail
   * is no longer rendered — it is logged to the debug console as each tool
   * completes. Buffered per live turn solely to assemble that log line.
   */
  toolName?: string;
  toolInput?: Record<string, unknown>;
  result?: unknown;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export interface IUseAssistantWebSocketReturn {
  messages: IConversationMessage[];
  isProcessing: boolean;
  /**
   * True only while the in-flight turn's agent run is live and abortable
   * (from `status:'thinking'` to turn end) for a client-initiated turn. Drives
   * the composer's send↔stop morph; false during the non-abortable working
   * window (optimistic send, mid-session compaction) and for bootstrap turns.
   */
  isAbortable: boolean;
  /**
   * True from `ws.onopen` until the server's `assistant_ready` lands —
   * i.e. while the rehydration handshake (`transcript_snapshot` →
   * `assistant_ready`) is in flight. The view can render a spinner during
   * this window; once `messages` populates from the snapshot, the spinner
   * is naturally suppressed.
   */
  isRehydrating: boolean;
  connectionStatus: ConnectionStatus;
  error: string | null;
  sendMessage: (content: string, attachments?: IObservationDocument[]) => void;
  /** Interrupt the in-flight turn (sends `stop_turn`). No-op if the WS is closed. */
  stopTurn: () => void;
}

/**
 * Custom hook for managing the WebSocket connection to the assistant server.
 *
 * Authentication is cookie-based: the browser attaches the httpOnly `_s`
 * session cookie to the WS upgrade automatically. The hook itself does not
 * receive or know any session credential. Callers pass `isAuthenticated`
 * (a boolean derived from their auth provider) to gate connection — a
 * `false` value keeps the socket disconnected, a `true → false` transition
 * tears it down, and a `false → true` transition reconnects. The server
 * (`packages/api/src/assistant/assistantWebSocket.ts`) reads `_s` from the
 * upgrade request's `Cookie` header and checks the `Origin` header against
 * `WEBAPP_URL` to defend against cross-site WebSocket hijacking.
 *
 * The server emits `transcript_snapshot` then `assistant_ready` on every
 * connect (and again after `/reset`). The hook gates outbound
 * `user_message` and `/reset` sends on the second frame — the server
 * rejects pre-handshake client messages with an `ASSISTANT_NOT_READY` error
 * that this hook swallows, since recovery is automatic once `assistant_ready`
 * lands.
 *
 * State / ref pairing: several pieces of state have a sibling ref
 * (`isProcessing` ↔ `turnInFlightRef`, `connectionStatus` is read by event
 * callbacks via `wsRef.current.readyState`, etc.). The state drives renders;
 * the ref provides a synchronous read from inside event callbacks where
 * setState has not yet committed.
 */
export const useAssistantWebSocket = (isAuthenticated: boolean): IUseAssistantWebSocketReturn => {
  const [messages, setMessages] = React.useState<IConversationMessage[]>([]);
  const [isProcessing, setIsProcessing] = React.useState(false);
  // True only while the in-flight turn's agent run is LIVE and therefore
  // abortable — i.e. from the server's `status:'thinking'` until the turn ends.
  // Distinct from `isProcessing`, which also covers the working-but-not-yet-
  // abortable window (optimistic send, and the silent mid-session compaction
  // before the agent run starts). Drives the composer's send↔stop morph: the
  // button is a stop button iff `isAbortable`, otherwise a normal send button.
  const [isAbortable, setIsAbortable] = React.useState(false);
  const [isRehydrating, setIsRehydrating] = React.useState(false);
  // Start optimistic. On a cold page load the WS upgrade only happens after
  // AuthProvider resolves `session`, and we don't want a "Disconnected" flash
  // covering that window. `connect()` flips to 'disconnected' if auth
  // resolves to logged-out; `ws.onclose` flips it if a real connection drops.
  const [connectionStatus, setConnectionStatus] = React.useState<ConnectionStatus>('connecting');
  const [error, setError] = React.useState<string | null>(null);

  const wsRef = React.useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  // FIFO queue of outbound user messages that arrived while the WS was
  // disconnected, before `assistant_ready` landed, or while a turn was
  // already in flight (mid-turn typing). Each entry carries the optimistic
  // bubble's `clientMessageId` so the server persists it on the matching
  // user row and future snapshots can dedupe by id, plus the per-bubble
  // `attachmentDocumentIds` the composer resolved before send.
  const pendingMessagesRef = React.useRef<
    Array<{ content: string; clientMessageId: string; attachmentDocumentIds?: string[] }>
  >([]);
  const isConnectingRef = React.useRef(false);
  const hasConnectedRef = React.useRef(false);
  // Drives `scheduleReconnect`'s backoff delay; resets on `ws.onopen` and on `online` events.
  const reconnectAttemptsRef = React.useRef(0);
  // Flips true on `assistant_ready` receipt; flips false on every `onclose`
  // and immediately before sending `/reset`. Gates outbound sends.
  const readyRef = React.useRef(false);
  // True while the server is actively processing a turn for this connection.
  // `sendMessage` consults this synchronously to decide whether to ship a
  // new frame or hold it in `pendingMessagesRef` for the next batch drain —
  // mid-turn typing must NOT race a second `user_message` onto the wire,
  // otherwise the server runs the follow-up as its own turn and the user
  // gets two replies for what they meant as one.
  const turnInFlightRef = React.useRef(false);
  // True only for a turn the client itself started (user send / queue flush).
  // Gates `isAbortable`: a server-initiated turn (the proactive bootstrap)
  // emits `status:'thinking'` with no preceding `startTurn`, so it never
  // promotes to abortable — stopping the bootstrap greeting is out of scope.
  const clientInitiatedTurnRef = React.useRef(false);
  const currentToolExecutionsRef = React.useRef<IToolExecution[]>([]);
  /**
   * Tracks the most recent assistant bubble's id within the current
   * turn. The server emits one `assistant_message` per LLM round that
   * wrote any prose — multi-round turns produce N bubbles in a row,
   * and round 2 must slot RIGHT BELOW round 1, even if mid-turn user
   * typing has populated the transcript with queued pending bubbles.
   * The id is cleared on `turn_complete` / `endTurn` so the next turn
   * anchors on its own user bubble(s).
   */
  const currentTurnLastAssistantIdRef = React.useRef<string | null>(null);

  /**
   * Id of the assistant bubble currently being grown by
   * `assistant_message_delta` frames, or `null` when no round is mid-stream.
   * A private id cache — NOT a second source of truth (the bubble's own
   * `isStreaming` flag is that) — so every `setMessages` updater stays pure:
   * append / finalize / drop target this id without scanning. Seeded on the
   * first delta of a round and cleared when the round's terminal
   * `assistant_message` finalizes the bubble. A non-null value at `endTurn`
   * means the stream never finalized (error / abort mid-round) and the partial
   * bubble is dropped.
   */
  const streamingAssistantIdRef = React.useRef<string | null>(null);

  /**
   * Mark the start of a new turn: clear prior tool state and flip the
   * in-flight flags. Called from both the immediate-send path and the
   * post-`assistant_ready` queue drain.
   */
  const startTurn = (): void => {
    currentToolExecutionsRef.current = [];
    currentTurnLastAssistantIdRef.current = null;
    streamingAssistantIdRef.current = null;
    setIsProcessing(true);
    turnInFlightRef.current = true;
    // Client-initiated turn: eligible to become abortable, but not yet — the
    // agent run only goes live (and `stop_turn` only does anything) once the
    // server's `status:'thinking'` lands. Until then the button stays a send
    // button so the working-but-not-yet-abortable window has no dead stop.
    clientInitiatedTurnRef.current = true;
    setIsAbortable(false);
  };

  /**
   * Mark the end of a turn: clear in-flight flags + tool state. Called
   * on `turn_complete`, when an error frame arrives, when a snapshot
   * lands (reconnect replaces all live state), and on close.
   */
  const endTurn = (): void => {
    turnInFlightRef.current = false;
    currentTurnLastAssistantIdRef.current = null;
    // Drop any unfinalized streaming bubble. On the success path the ref is
    // already null (snapped on `assistant_message`); a non-null value here is
    // an error / abort mid-stream, where the half-streamed bubble would
    // otherwise linger looking like a complete (truncated) reply. Errored turns
    // are excluded from the server snapshot, so dropping it matches rehydration.
    const streamingId = streamingAssistantIdRef.current;
    if (streamingId !== null) {
      setMessages((prev) => dropStreamingBubble(prev, streamingId));
      streamingAssistantIdRef.current = null;
    }
    setIsProcessing(false);
    clientInitiatedTurnRef.current = false;
    setIsAbortable(false);
    currentToolExecutionsRef.current = [];
  };

  /**
   * Drain the pending queue as a single turn. Always emits one
   * `user_message` frame carrying the queued entries as a `messages` array
   * (N = 1 or N ≥ 2 — server treats both identically and persists N user
   * rows either way). Flips the matching bubbles from queued
   * (`isInFlight: false`) to in-flight (`isInFlight: true`) so the view
   * moves them above the thinking box. No-op if the WS is not OPEN +
   * ready, the queue is empty, or a turn is already in flight (the
   * current turn must finish before we drain — that's what gives us the
   * batching window).
   */
  const flushPendingQueueRef = React.useRef<() => void>(() => {});
  flushPendingQueueRef.current = () => {
    if (pendingMessagesRef.current.length === 0) {
      return;
    }
    if (wsRef.current?.readyState !== WebSocket.OPEN || !readyRef.current) {
      return;
    }
    if (turnInFlightRef.current) {
      return;
    }

    const queued = pendingMessagesRef.current;
    pendingMessagesRef.current = [];
    const queuedIds = new Set(queued.map((q) => q.clientMessageId));
    setMessages((prev) =>
      prev.map((m) =>
        m.clientMessageId !== undefined && queuedIds.has(m.clientMessageId) ? { ...m, isInFlight: true } : m
      )
    );
    startTurn();
    wsRef.current.send(JSON.stringify({ type: 'user_message', messages: queued }));
  };

  const handleServerMessageRef = React.useRef<(data: ServerMessage) => void>(() => {});
  handleServerMessageRef.current = (data: ServerMessage) => {
    switch (data.type) {
      case 'status':
        if (data.status === 'thinking') {
          setIsProcessing(true);
          // Gate user sends from this point — the server has signaled
          // that a turn is in flight. Necessary for SERVER-INITIATED
          // turns (the proactive session bootstrap is one): without this
          // flip, `canSendNow` in `sendMessage` would be true and a user
          // typing mid-bootstrap would ship their message immediately,
          // landing the optimistic bubble above the still-pending
          // assistant reply. For client-initiated turns this is a no-op
          // — `startTurn` (in `sendMessage`) already flipped the ref
          // true before the user-frame went out.
          turnInFlightRef.current = true;
          // The agent run is now live → the turn is abortable, but ONLY for a
          // turn the client started. `thinking` fires after any silent
          // mid-session compaction, so this also closes the non-abortable
          // compaction window. Bootstrap (no `startTurn`) stays non-abortable.
          if (clientInitiatedTurnRef.current) {
            setIsAbortable(true);
          }
        }
        break;

      case 'tool_started': {
        const newTool: IToolExecution = {
          id: data.toolId,
          toolName: data.toolName,
          toolInput: data.toolInput,
          status: 'running' as const,
        };
        currentToolExecutionsRef.current = [...currentToolExecutionsRef.current, newTool];
        break;
      }

      case 'tool_completed': {
        const idx = currentToolExecutionsRef.current.findIndex((t) => t.id === data.toolId && t.status === 'running');
        if (idx >= 0) {
          const tool: IToolExecution = {
            ...currentToolExecutionsRef.current[idx],
            status: 'complete',
            result: data.result,
          };
          const next = [...currentToolExecutionsRef.current];
          next[idx] = tool;
          currentToolExecutionsRef.current = next;
          // The server sends tool name/args/output only to debug-assistant
          // viewers, so a populated `toolName` is the signal to log. One row per
          // tool, the moment it completes.
          if (tool.toolName !== undefined) {
            // eslint-disable-next-line no-console -- the debug-assistant gate exists to surface tool detail in the console
            console.debug(
              '[assistant] %c%s%c tool call',
              'font-weight: bold',
              tool.toolName,
              'font-weight: normal',
              tool
            );
          }
        }
        break;
      }

      case 'assistant_message_delta': {
        const streamingId = streamingAssistantIdRef.current;
        if (streamingId === null) {
          // First delta of the round: seed a streaming bubble at the same slot a
          // finalized reply would land (so multi-round / mid-turn-typing ordering
          // reuses the one tested insertion path).
          const streamingRow: IConversationMessage = {
            id: `msg-${generateId()}`,
            role: 'assistant',
            content: data.text,
            timestamp: new Date(),
            isStreaming: true,
          };
          const previousAssistantId = currentTurnLastAssistantIdRef.current;
          setMessages((prev) => insertAssistantReply(prev, streamingRow, { previousAssistantId }));
          streamingAssistantIdRef.current = streamingRow.id;
        } else {
          setMessages((prev) => appendStreamingDelta(prev, streamingId, data.text));
        }
        break;
      }

      case 'assistant_message': {
        const streamingId = streamingAssistantIdRef.current;
        let assistantId: string;
        if (streamingId !== null) {
          // Finalize the bubble grown from this round's deltas: snap to the
          // authoritative full text (drops any client-side accumulation drift)
          // and clear its streaming flag.
          assistantId = streamingId;
          setMessages((prev) => finalizeStreamingBubble(prev, streamingId, data.content));
          streamingAssistantIdRef.current = null;
        } else {
          // No deltas streamed for this round (e.g. a round that produced text
          // without per-delta events) — create the bubble outright, as before.
          const assistantRow: IConversationMessage = {
            id: `msg-${generateId()}`,
            role: 'assistant',
            content: data.content,
            timestamp: new Date(),
          };
          assistantId = assistantRow.id;
          const previousAssistantId = currentTurnLastAssistantIdRef.current;
          setMessages((prev) => insertAssistantReply(prev, assistantRow, { previousAssistantId }));
        }
        // Round boundary: clear the per-round tool buffer so the next round's
        // tool_completed logs start fresh.
        currentToolExecutionsRef.current = [];
        // Remember this bubble so a subsequent round in the same turn slots
        // immediately below it (see `insertAssistantReply`'s continuation branch).
        currentTurnLastAssistantIdRef.current = assistantId;
        break;
      }

      case 'turn_complete': {
        // Terminal frame for the turn. Confirm every pending bubble whose
        // id participated in this turn, then close out turn state and
        // drain any mid-turn typed-message queue as a new batched turn.
        // Server-initiated turns (bootstrap) omit `clientMessageIds`;
        // `confirmPendingMessages` returns input unchanged for an empty
        // array.
        const confirmingIds = data.clientMessageIds ?? [];
        setMessages((prev) => confirmPendingMessages(prev, confirmingIds));
        endTurn();
        // No-op if the queue is empty; otherwise starts the next turn
        // immediately, flipping isProcessing back to true in the same
        // React batch so the spinner doesn't flicker.
        flushPendingQueueRef.current();
        break;
      }

      case 'error':
        // Swallow `ASSISTANT_NOT_READY` — the server rejected a frame that
        // raced the handshake. Recovery is automatic once the next
        // `assistant_ready` lands and the FIFO queue flushes. Surfacing the
        // raw protocol message to a user who did nothing wrong is noise.
        if (data.code === 'ASSISTANT_NOT_READY') {
          // eslint-disable-next-line no-console
          console.warn('Assistant rejected pre-handshake message; will retry after assistant_ready', {
            message: data.message,
          });
          break;
        }
        setError(data.message);
        endTurn();
        break;

      case 'transcript_snapshot': {
        // Replace messages with snapshot-derived rows, preserving any
        // in-flight optimistic bubbles not represented in the snapshot. The
        // reconciler matches by `clientMessageId` identity.
        const snapshotMessages = snapshotToConversation(data.messages, generateId);
        // The reconciler keeps only pending bubbles, so a mid-stream streaming
        // bubble (not pending) is already evicted here; the `endTurn` below then
        // clears the streaming ref (its `dropStreamingBubble` is a harmless no-op
        // against the replaced list).
        setMessages((prev) => reconcileSnapshotMessages(prev, snapshotMessages, data.messages));
        // Any prior connection's live-stream state is gone — a snapshot
        // arriving mid-turn after a reconnect must not leave a stale
        // "Executing tool..." spinner on screen.
        endTurn();
        break;
      }

      case 'thread_reset':
        // Drop only pending optimistic bubbles (the typed `/reset`); keeping
        // confirmed history on screen avoids an empty-list scroll jump before the
        // rebind's snapshot lands. Gate sends until the next `assistant_ready`.
        readyRef.current = false;
        setMessages((prev) => prev.filter((m) => m.isPending !== true));
        endTurn();
        break;

      case 'assistant_ready':
        readyRef.current = true;
        setIsRehydrating(false);
        // Drain any messages the user typed while we were reconnecting /
        // pre-handshake. The drain coalesces multi-entry queues into one
        // `user_message` frame so the LLM sees them as one turn.
        flushPendingQueueRef.current();
        break;

      default:
        break;
    }
  };

  /** Schedule the next reconnect with exponential backoff; no-op when `navigator.onLine` is false. */
  const scheduleReconnect = (): void => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (!navigator.onLine) {
      debugLog('scheduleReconnect skipped — browser is offline; waiting for `online` event');
      return;
    }
    const attempt = reconnectAttemptsRef.current;
    reconnectAttemptsRef.current = attempt + 1;
    const delay = Math.min(RECONNECT_INITIAL_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
    debugLog('scheduleReconnect armed', { attempt: attempt + 1, delayMs: delay });
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      connectRef.current();
    }, delay);
  };

  const connectRef = React.useRef<() => void>(() => {});
  connectRef.current = () => {
    if (!isAuthenticated || isConnectingRef.current) {
      if (!isAuthenticated) {
        setConnectionStatus('disconnected');
      }
      return;
    }

    // Skip the WS construction when the browser knows it's offline. The
    // `online` listener in the mount effect calls back here the moment the
    // network returns. `navigator.onLine === false` is authoritative; `true`
    // is best-effort, so we still attempt and let the real connection
    // succeed or fail on its own.
    if (!navigator.onLine) {
      debugLog('connect skipped — browser is offline; waiting for `online` event');
      setConnectionStatus('disconnected');
      return;
    }

    isConnectingRef.current = true;
    readyRef.current = false;

    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }

    setConnectionStatus('connecting');
    setError(null);

    try {
      // Derive the WebSocket origin from the API base URL when it's set explicitly,
      // or from `window.location` when the build targets same-origin (CloudFront deploys
      // set `VITE_API_SERVER_URL=''` so all API calls become relative).
      const apiUrl = getApiBaseUrl();
      let wsOrigin: string;
      if (apiUrl) {
        const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
        wsOrigin = `${wsProtocol}://${apiUrl.replace(/^https?:\/\//, '')}`;
      } else {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        wsOrigin = `${wsProtocol}://${window.location.host}`;
      }
      // Auth comes from the `_s` cookie attached by the browser; the server
      // also enforces an Origin allowlist on the upgrade. The user's timezone
      // is no longer passed on the URL — the backend reads it from the user
      // record, which `useTimezoneSync` keeps current on every app load.
      const wsUrl = `${wsOrigin}/ws/assistant`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        isConnectingRef.current = false;
        hasConnectedRef.current = true;
        reconnectAttemptsRef.current = 0;
        setConnectionStatus('connected');
        setError(null);
        // Rehydration handshake (`transcript_snapshot` → `assistant_ready`)
        // starts now; the view renders a spinner while this is in flight on
        // a cold connect (suppressed once messages populate from the
        // snapshot). Sending is gated on `assistant_ready`; the queue
        // drains from `handleServerMessageRef` when the handshake completes.
        setIsRehydrating(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string) as ServerMessage;
          handleServerMessageRef.current(data);
        } catch {
          // Silently ignore malformed messages
        }
      };

      ws.onclose = (event) => {
        debugLog('socket closed', describeCloseEvent(event));
        isConnectingRef.current = false;
        readyRef.current = false;
        setConnectionStatus('disconnected');
        setIsRehydrating(false);
        endTurn();

        if (event.code === 4001) {
          setError('Authentication failed');
        } else if (event.code === 4003) {
          // The server emits 4003 when the user lacks the `feat-assistant`
          // gate. The frontend route gate (`AssistantRouteGate`) usually
          // hides the entry point first; this surfaces if the gate changes
          // mid-session or in the rare race where the user reaches the
          // pane before flags refresh.
          setError('Assistant access not enabled');
        } else if (event.code !== 1000) {
          scheduleReconnect();
        }
      };

      ws.onerror = () => {
        isConnectingRef.current = false;
        // Only show error if we had a connection that dropped — not during
        // initial connection.
        if (hasConnectedRef.current) {
          setConnectionStatus('error');
          setError('Connection error');
        }
      };
    } catch (err) {
      isConnectingRef.current = false;
      setConnectionStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to connect');
    }
  };

  const connect = React.useCallback(() => {
    connectRef.current();
  }, []);

  const sendMessage = React.useCallback(
    (content: string, attachments?: IObservationDocument[]) => {
      // Derive the wire ids from the attachments array; the two are
      // structurally redundant (`ids = attachments.map(a => a.id)`), so
      // accepting both at the call site would be double-bookkeeping.
      const attachmentList = attachments ?? [];
      const ids = attachmentList.map((a) => a.id);
      if (!content.trim() && ids.length === 0) {
        return;
      }

      // The optimistic bubble's `clientMessageId` is also the wire id —
      // server persists it on the resulting user row and re-emits it on
      // future `transcript_snapshot`s, so the snapshot reconciler can
      // dedupe by identity.
      const clientMessageId = crypto.randomUUID();

      // Three-way gate:
      //  - WS not OPEN OR not handshake-ready → queue (drained on
      //    `assistant_ready`).
      //  - A turn is already in flight → queue (drained on the current
      //    turn's `assistant_message`). This is the load-bearing branch
      //    that batches mid-turn typing into the next turn.
      //  - Otherwise → send immediately as a single-message turn.
      const wsOpen = wsRef.current?.readyState === WebSocket.OPEN;
      const canSendNow = wsOpen && readyRef.current && !turnInFlightRef.current;

      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${generateId()}`,
          role: 'user',
          content,
          timestamp: new Date(),
          clientMessageId,
          isPending: true,
          // In-flight from this moment if we're sending now; queued
          // otherwise. The view splits these two states across the
          // thinking box.
          isInFlight: canSendNow,
          ...(attachmentList.length > 0 ? { attachments: attachmentList } : {}),
        },
      ]);

      setError(null);

      const wirePart: { content: string; clientMessageId: string; attachmentDocumentIds?: string[] } = {
        content,
        clientMessageId,
        ...(ids.length > 0 ? { attachmentDocumentIds: ids } : {}),
      };

      if (canSendNow) {
        startTurn();
        // One-entry `messages` array — the unified wire shape. The server
        // treats N = 1 identically to a batched N ≥ 2 frame.
        wsRef.current!.send(JSON.stringify({ type: 'user_message', messages: [wirePart] }));
        return;
      }

      // Queue for the next drain. Trigger a connect when the WS isn't
      // open; do NOT connect when a turn is in flight (would kill the
      // current turn).
      pendingMessagesRef.current = [...pendingMessagesRef.current, wirePart];
      if (!wsOpen) {
        connect();
      }
    },
    [connect]
  );

  /**
   * Interrupt the in-flight turn. Sends the `stop_turn` control frame; the
   * server aborts the live agent run, persists the completed rounds, and emits
   * the turn's terminal `turn_complete` — which runs the normal `endTurn` +
   * queue-drain path, so any messages the user queued while the turn ran flush
   * as the next turn. No optimistic local state change: the button is only a
   * stop button while `isAbortable`, and `turn_complete` clears that.
   */
  const stopTurn = React.useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: CLIENT_MESSAGE_TYPE.StopTurn }));
    }
  }, []);

  // Connect on mount and when session token changes.
  //
  // The WS construction is deferred to a microtask so React 18 StrictMode's
  // synchronous setup → cleanup → setup double-invoke does not create a
  // throwaway WebSocket. Without the defer, the first setup constructs a WS
  // in CONNECTING state, the immediate cleanup calls `ws.close(1000)` — but
  // the browser ignores the close arg while CONNECTING and reports the
  // close as code 1006 (abnormal). That trips the `event.code !== 1000`
  // reconnect branch in `ws.onclose` and a zombie 3-second reconnect timer
  // races the legitimate connection from the second setup, producing two
  // simultaneous server-side WS sessions for the same user.
  React.useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) {
        return;
      }
      connectRef.current();
    });

    // Recovery skips the backoff timer when the browser hands us a real signal.
    const handleOnline = (): void => {
      if (cancelled) {
        debugLog('online event ignored — effect already cleaned up');
        return;
      }
      const ws = wsRef.current;
      const readyState = ws?.readyState;
      if (ws && (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING)) {
        debugLog('online event — no-op (WS already live)', { readyState });
        return;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      debugLog('online event — reconnecting immediately', { readyState: readyState ?? 'no-ws' });
      connectRef.current();
    };
    // Browsers (and DevTools "Offline" mode) don't necessarily close an idle
    // WebSocket when the network drops — the socket sits at `readyState OPEN`
    // until the next write fails, which never happens for a receive-only
    // connection. Close it proactively so `onclose` runs and the UI status
    // flips to disconnected; `handleOnline` will reconnect when the network
    // returns. Use code 1000 so the close handler skips its own
    // reconnect-scheduling branch — `handleOnline` is the recovery path here.
    const handleOffline = (): void => {
      debugLog('offline event received');
      const ws = wsRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close(1000, 'Browser reports offline');
      }
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [isAuthenticated]);

  return {
    messages,
    isProcessing,
    isAbortable,
    isRehydrating,
    connectionStatus,
    error,
    sendMessage,
    stopTurn,
  };
};
